import { GoabLink } from '@abgov/angular-components';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  isDevMode,
  output,
} from '@angular/core';
import { Params, RouterLink, UrlTree } from '@angular/router';
import { ResponsiveService } from '../../services/responsive.service';

export type BackNavCtaSize = 'compact' | 'normal';
export type BackNavCtaMode = 'router' | 'href' | 'action';

@Component({
  selector: 'common-back-nav-cta',
  standalone: true,
  imports: [GoabLink, RouterLink],
  host: {
    '[attr.aria-label]': 'resolvedAriaLabel()',
  },
  templateUrl: './back-nav-cta.component.html',
  styleUrl: './back-nav-cta.component.scss',
})
export class BackNavCtaComponent {
  private readonly responsive = inject(ResponsiveService);

  label = input.required<string>();
  ariaLabel = input<string | undefined>(undefined, {
    alias: 'aria-label',
  });
  dataTestId = input<string | undefined>(undefined, {
    alias: 'data-testid',
  });
  size = input<BackNavCtaSize | undefined>(undefined);
  mt = input<GoabLink['mt']>();
  mb = input<GoabLink['mb']>();
  routerLink = input<string | readonly unknown[] | UrlTree | null>(null);
  queryParams = input<Params | undefined>();
  fragment = input<string | undefined>();
  href = input<string | undefined>();

  backNavRequested = output<void>();

  protected readonly resolvedAriaLabel = computed(
    () => this.ariaLabel() ?? this.label()
  );

  protected readonly hasRouterLink = computed(
    () => this.routerLink() !== null && this.routerLink() !== undefined
  );

  protected readonly resolvedBackNavSize = computed<BackNavCtaSize>(() => {
    const providedSize = this.size();
    if (providedSize) {
      return providedSize;
    }

    return this.responsive.isXSmall() ? 'compact' : 'normal';
  });

  protected readonly resolvedLinkSize = computed<GoabLink['size']>(() =>
    this.resolvedBackNavSize() === 'compact' ? 'small' : 'medium'
  );

  protected readonly hasHref = computed(() => Boolean(this.href()));

  protected readonly mode = computed<BackNavCtaMode>(() => {
    if (this.hasRouterLink()) {
      return 'router';
    }

    if (this.hasHref()) {
      return 'href';
    }

    return 'action';
  });

  // Keep semantics strict: exactly one behavior mode per instance.
  private readonly validateContract = effect(() => {
    if (!isDevMode()) {
      return;
    }

    const hasRouter = this.hasRouterLink();
    const hasHref = this.hasHref();
    const hasQueryParams = this.queryParams() !== undefined;
    const hasFragment = this.fragment() !== undefined;

    if (hasRouter && hasHref) {
      throw new Error(
        '[BackNavCtaComponent] Provide either routerLink or href, not both.'
      );
    }

    if (!hasRouter && (hasQueryParams || hasFragment)) {
      throw new Error(
        '[BackNavCtaComponent] queryParams/fragment require routerLink.'
      );
    }
  });

  handleButtonClick(): void {
    this.backNavRequested.emit();
  }
}
